import React, { useState, useEffect } from 'react';

// # LIBS
import axios from 'axios';
import Cookies from 'js-cookie';

// # DND KIT
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Badge,
  Link,
  TextField,
  Switch,
} from '@material-ui/core';

import { useSnackbar } from 'notistack';

// # UTILS
import currencyFormater from '../../utils/currencyFormater';
import { axiosPatchRequest } from '../../utils/axiosRequests';

const columns = [
  { id: 'image', label: 'Image', minWidth: 100, align: 'left' },
  { id: 'name', label: 'Nom', minWidth: 100, align: 'left' },
  { id: 'ref', label: 'Référence', minWidth: 100, align: 'left' },
  {
    id: 'category',
    label: 'Rubrique',
    minWidth: 100,
    align: 'left',
  },
  {
    id: 'subcategory',
    label: 'Sous-rubrique',
    minWidth: 100,
    align: 'left',
  },
  {
    id: 'price',
    label: 'Prix',
    minWidth: 100,
    align: 'center',
  },
  {
    id: 'tax',
    label: 'TVA',
    minWidth: 100,
    align: 'center',
  },
  {
    id: 'inventory',
    label: 'Stock',
    minWidth: 100,
    align: 'center',
  },
  {
    id: 'status',
    label: 'Status',
    minWidth: 100,
    align: 'right',
  },
  {
    id: '',
    label: '',
    minWidth: 100,
    align: 'center',
  },
];

const columnsBis = [
  { id: 'ordre', label: 'Ordre', maxWidth: 50, align: 'center' },
  { id: 'image', label: 'Image', minWidth: 100, align: 'left' },
  { id: 'name', label: 'Nom', minWidth: 100, align: 'left' },
  { id: 'ref', label: 'Référence', minWidth: 100, align: 'left' },
  {
    id: 'category',
    label: 'Rubrique',
    minWidth: 100,
    align: 'left',
  },
  {
    id: 'subcategory',
    label: 'Sous-rubrique',
    minWidth: 100,
    align: 'left',
  },
  {
    id: 'price',
    label: 'Prix',
    minWidth: 100,
    align: 'center',
  },
  {
    id: 'tax',
    label: 'TVA',
    minWidth: 100,
    align: 'center',
  },
  {
    id: 'inventory',
    label: 'Stock',
    minWidth: 100,
    align: 'center',
  },
  {
    id: 'status',
    label: 'Status',
    minWidth: 100,
    align: 'center',
  },
];

const useStyles = makeStyles({
  root: {
    width: '100%',
  },
  container: {
    maxHeight: 'fit-content',
  },
  div: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '1rem',
  },
  switch: {
    marginLeft: '30px',
  },
  sortableRow: {
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing',
    },
  },
  dragHandle: {
    cursor: 'grab',
    padding: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    color: '#666',
    '&:active': {
      cursor: 'grabbing',
    },
  },
});

// # SORTABLE ROW COMPONENT
function SortableRow({
  product,
  index,
  classes,
  state,
  handleClick,
  handleStockChange,
  handleStockValidation,
  handleChange,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: index,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style} className={classes.sortableRow} {...attributes}>
      {state.isReorder && (
        <TableCell align="center">
          <div className={classes.dragHandle} {...listeners}>
            ⋮⋮
          </div>
        </TableCell>
      )}
      <TableCell align="left">
        <img src={`${process.env.REACT_APP_AWS}/${product.photo}`} alt={product.name} width="50" height="50" />
      </TableCell>
      <TableCell align="left">
        <Link href={`/admin/products/${product._id}`}>{product.name}</Link>
      </TableCell>
      <TableCell align="left">{product.reference}</TableCell>
      <TableCell align="left">{product.category.name}</TableCell>
      <TableCell align="left">{product.subCategory.name}</TableCell>
      <TableCell align="center">{currencyFormater(product.price)}</TableCell>
      <TableCell align="center">{product.tvaRate.amount} %</TableCell>
      <TableCell align="center" onClick={() => handleClick(product.id, index)}>
        {product.isRestock ? (
          <TextField
            key={index}
            variant="outlined"
            value={product.stock}
            onChange={(e) => handleStockChange(index, product._id, e)}
            onKeyDown={(e) => handleStockValidation(index, product._id, e)}
          />
        ) : (
          product.stock
        )}
      </TableCell>
      <TableCell align="center" colSpan={2}>
        <Badge
          badgeContent={product.isActive ? 'Actif' : 'Inactif'}
          color={product.isActive ? 'primary' : 'error'}
          onChange={() => handleChange(product.id, index)}
        />
        <Switch
          className={classes.switch}
          checked={product.isActive}
          onChange={() => handleChange(product.id, index)}
          name="checkedB"
          color={product.isActive ? 'primary' : 'secondary'}
        />
      </TableCell>
    </TableRow>
  );
}

export default function ProductTable(props) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const token = Cookies.get('jwt');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [state, setState] = useState({
    isReorder: false,
    products: props.data.sort((a, b) => a.position - b.position),
    updateProduct: [],
  });

  // # DND SENSORS
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleClick = (id, idx) => {
    const temp = state.products[idx];
    temp.isRestock = true;

    setState((prev) => ({
      ...prev,
      products: prev.products.map((el, index) => (index === idx ? temp : el)),
    }));
  };

  const handleStockChange = (idx, id, e) => {
    const temp = state.products[idx];
    temp.stock = e.target.value;

    setState((prev) => ({
      ...prev,
      products: prev.products.map((el, index) => (index === idx ? temp : el)),
    }));
  };

  const handleStockValidation = async (idx, id, e) => {
    if (e.keyCode !== 13) {
      return;
    }

    const temp = state.products[idx];
    temp.isRestock = false;

    setState((prev) => ({
      ...prev,
      products: prev.products.map((el, index) => (index === idx ? temp : el)),
    }));

    await axios.patch(`${process.env.REACT_APP_API}/products/${id}`, temp, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const handleChange = (id, idx) => {
    const temp = state.products[idx];
    temp.isActive = !temp.isActive;

    axiosPatchRequest(`/products/status/${id}`, token, { isActive: temp.isActive })
      .then(() => {
        setState((prev) => ({
          ...prev,
          products: prev.products.map((el, index) => (index === idx ? temp : el)),
        }));
      })
      .catch((err) => {
        console.log(err.response);
      });
  };

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      products: props.data.sort((a, b) => a.position - b.position),
    }));
    if (props.subcategory !== 'all') {
      setState((prev) => ({
        ...prev,
        isReorder: true,
      }));
    } else {
      setState((prev) => ({
        ...prev,
        isReorder: false,
      }));
    }
  }, [props]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // # HANDLE DRAG END
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = active.id;
    const newIndex = over.id;

    if (oldIndex === newIndex) {
      return;
    }

    // Store original products for potential revert
    const originalProducts = [...state.products];

    // Create a map of original positions for comparison
    const originalPositions = new Map();
    originalProducts.forEach((product) => {
      originalPositions.set(product._id, product.position);
    });

    // Update products order
    const reorderedProducts = arrayMove(originalProducts, oldIndex, newIndex);

    // Update positions based on new order
    const updatedProducts = reorderedProducts.map((product, index) => ({
      ...product,
      position: index + 1,
    }));

    // Track changes for API call - find all products whose position changed
    const changingProducts = updatedProducts
      .map((product) => {
        const originalPosition = originalPositions.get(product._id);
        if (originalPosition !== product.position) {
          return {
            id: product._id,
            position: product.position,
          };
        }
        return null;
      })
      .filter((change) => change !== null);

    // Update state immediately for responsive UI
    setState((prev) => ({
      ...prev,
      products: updatedProducts,
    }));

    // Auto-save changes to API
    if (changingProducts.length > 0) {
      try {
        await axios.patch(`${process.env.REACT_APP_API}/products`, changingProducts, {
          headers: {
            authorization: `Bearer ${token}`,
          },
        });
        enqueueSnackbar("L'ordre des produits a été mis à jour", {
          variant: 'success',
        });
      } catch (err) {
        console.log(err.response);
        enqueueSnackbar("Une erreur s'est produite lors de la mise à jour", {
          variant: 'error',
        });
        // Revert to original order on error
        setState((prev) => ({
          ...prev,
          products: originalProducts,
        }));
      }
    }
  };

  return (
    <>
      <Paper className={classes.root}>
        <TableContainer className={classes.container}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              {state.isReorder ? (
                <TableRow>
                  {columnsBis.map((column, index) => (
                    <TableCell key={index} align={column.align} style={{ minWidth: column.minWidth }}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              ) : (
                <TableRow>
                  {columns.map((column, index) => (
                    <TableCell key={index} align={column.align} style={{ minWidth: column.minWidth }}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {state.isReorder ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext
                    items={state.products
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((_, idx) => page * rowsPerPage + idx)}
                    strategy={verticalListSortingStrategy}
                  >
                    {state.products.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((product, idx) => {
                      const actualIndex = page * rowsPerPage + idx;
                      return (
                        <SortableRow
                          key={product._id}
                          product={product}
                          index={actualIndex}
                          classes={classes}
                          state={state}
                          handleClick={handleClick}
                          handleStockChange={handleStockChange}
                          handleStockValidation={handleStockValidation}
                          handleChange={handleChange}
                        />
                      );
                    })}
                  </SortableContext>
                </DndContext>
              ) : (
                state.products.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((product, idx) => (
                  <TableRow key={product._id || idx}>
                    <TableCell align="left">
                      <img
                        src={`${process.env.REACT_APP_AWS}/${product.photo}`}
                        alt={product.name}
                        width="50"
                        height="50"
                      />
                    </TableCell>
                    <TableCell align="left">
                      <Link href={`/admin/products/${product._id}`}>{product.name}</Link>
                    </TableCell>
                    <TableCell align="left">{product.reference}</TableCell>
                    <TableCell align="left">{product?.category?.name ?? ''}</TableCell>
                    <TableCell align="left">{product?.subCategory?.name ?? ''}</TableCell>
                    <TableCell align="center">{currencyFormater(product.price)}</TableCell>
                    <TableCell align="center">{product?.tvaRate?.amount ?? ''} %</TableCell>
                    <TableCell align="center" onClick={() => handleClick(product.id, idx)}>
                      {product.isRestock ? (
                        <TextField
                          key={idx}
                          variant="outlined"
                          value={product.stock}
                          onChange={(e) => handleStockChange(idx, product._id, e)}
                          onKeyDown={(e) => handleStockValidation(idx, product._id, e)}
                        />
                      ) : (
                        product.stock
                      )}
                    </TableCell>
                    <TableCell align="center" colSpan={2}>
                      <Badge
                        badgeContent={product.isActive ? 'Actif' : 'Inactif'}
                        color={product.isActive ? 'primary' : 'error'}
                        onChange={() => handleChange(product.id, idx)}
                      />
                      <Switch
                        className={classes.switch}
                        checked={product.isActive}
                        onChange={() => handleChange(product.id, idx)}
                        name="checkedB"
                        color={product.isActive ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[50, 100]}
          component="div"
          count={state.products.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </>
  );
}
